import * as React from 'react'
import { useForm } from 'react-hook-form'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescriptionHidden,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  useCreatePortalContact,
  useUpdatePortalContact,
} from '@/hooks/use-portal'
import { showSuccess } from '@/lib/toast'
import {
  type ContactTitle,
  JOB_TITLE_OPTIONS,
} from '@/lib/schemas/contact.schema'
import { z } from 'zod'
import type { PortalContact } from '@/services/portal.service'

const portalContactSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(255),
  lastName: z.string().max(255).optional().or(z.literal('')),
  email: z.string().email('Invalid email address').optional().or(z.literal('')),
  phone: z
    .string()
    .max(50)
    .regex(
      /^[0-9+\-()\s]*$/,
      'Phone can only contain numbers, +, -, (, ), and spaces'
    )
    .optional()
    .or(z.literal('')),
  title: z.string().min(1, 'Job title is required'),
})

type PortalContactFormValues = z.infer<typeof portalContactSchema>

interface PortalAddContactModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode?: 'create' | 'edit'
  contact?: PortalContact
  forcePrimaryContact?: boolean
  onSuccess?: () => void
}

export function PortalAddContactModal({
  open,
  onOpenChange,
  mode = 'create',
  contact,
  forcePrimaryContact = false,
  onSuccess,
}: PortalAddContactModalProps) {
  const isEditMode = mode === 'edit'

  const form = useForm<PortalContactFormValues>({
    resolver: standardSchemaResolver(portalContactSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      title: '',
    },
  })

  const createContact = useCreatePortalContact()
  const updateContact = useUpdatePortalContact()

  React.useEffect(() => {
    if (!open) {
      form.reset()
      return
    }

    if (isEditMode && contact) {
      form.reset({
        firstName: contact.firstName ?? '',
        lastName: contact.lastName ?? '',
        email: contact.email ?? '',
        phone: contact.phone ?? '',
        title: contact.title ?? '',
      })
    }
  }, [open, isEditMode, contact, form])

  const onSubmit = async (values: PortalContactFormValues) => {
    try {
      const payload = {
        firstName: values.firstName.trim(),
        lastName: values.lastName?.trim() || undefined,
        email: values.email?.trim() || undefined,
        phone: values.phone?.trim() || undefined,
        title: values.title as ContactTitle,
      }

      if (isEditMode) {
        if (!contact) throw new Error('Missing contact')
        await updateContact.mutateAsync({
          contactId: contact.id,
          data: payload,
        })
        showSuccess('Contact updated successfully')
      } else {
        await createContact.mutateAsync({
          ...payload,
          isPrimaryContact: forcePrimaryContact,
        })
        showSuccess('Contact created successfully')
      }

      onOpenChange(false)
      onSuccess?.()
    } catch {
      // Error toast is shown by the axios interceptor — swallow here so the
      // modal stays open without firing a duplicate notification.
    }
  }

  const isSubmitting = createContact.isPending || updateContact.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-w-150 px-4 py-5')}>
        <DialogHeader>
          <DialogTitle className="text-balance">
            {isEditMode ? 'Edit Contact' : 'Add New Contact'}
          </DialogTitle>
          <DialogDescriptionHidden>
            {isEditMode
              ? 'Update contact details'
              : 'Fill out the form to create a new contact'}
          </DialogDescriptionHidden>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-4">
              <div className="flex gap-3">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem className="flex flex-1 flex-col">
                      <FormLabel>
                        First Name
                        <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter first name"
                          autoComplete="given-name"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem className="flex flex-1 flex-col">
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter last name"
                          autoComplete="family-name"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex gap-3">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="flex flex-1 flex-col">
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="Enter email address"
                          autoComplete="email"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem className="flex flex-1 flex-col">
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <Input
                          type="tel"
                          placeholder="Enter phone number"
                          autoComplete="tel"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>
                      Job Title
                      <span className="text-destructive">*</span>
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Job Title" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {JOB_TITLE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline-muted"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? 'Saving...'
                  : isEditMode
                    ? 'Save Contact'
                    : 'Create Contact'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
